import {
  Box,
  Image,
  Text,
  Button,
  Heading,
  ButtonProps,
  Skeleton,
  Flex,
  Container,
} from '@everlywell/ui-kit';
import MultipleCardsSwiper from 'components/MultipleCardsSwiper';
import ProductCardV2 from 'components/ProductCardV2';
import RichTextWrapper from 'components/RichTextWrapper';
import { Link, navigate } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { isFullUrl } from 'utils/helpers';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import useMdpModals from 'utils/hooks/useMdpModals';
import { ImageType } from 'utils/types';
import { CallToAction, ContentfulRichText } from 'utils/types/contentfulTypes';

import ProductTag from '../components/ProductTag';
import SectionContainer from '../components/SectionContainer';
import SectionHeader from '../components/SectionHeader';

export type MultipleCardsCarouselCard = {
  tags?: readonly Queries.Maybe<Queries.ContentfulFlag001>[] | null;
  title?: string | null;
  description?: string | null;
  cta?: CallToAction | null;
  mobileImage?: ImageType | null;
  desktopImage?: ImageType | null;
};

export type MultipleCardsCarouselProps = {
  eyebrow?: string | null;
  title?: string | null;
  cards?: MultipleCardsCarouselCard[] | null;
  disclaimer?: ContentfulRichText | null;
};

type HandleClickParams = {
  clickEvent: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>;
  label?: string;
  cardTitle?: string | null;
  ctaUrl?: string;
};

/**
 * MultipleCardsCarousel component
 * Component to display multiple cards in a carousel
 * @param {MultipleCardsCarouselProps} props
 * @param {string} [props.eyebrow] - The eyebrow text.
 * @param {string} [props.title] - The title text.
 * @param {MultipleCardsCarouselCard[]} [props.cards] - The cards to display.
 * @param {MultipleCardsCarouselCard} [props.cards.tag] - The tag for the card.
 * @param {MultipleCardsCarouselCard} [props.cards.title] - The title for the card.
 * @param {MultipleCardsCarouselCard} [props.cards.description] - The description for the card.
 * @param {MultipleCardsCarouselCard} [props.cards.cta] - The call to action for the card.
 * @param {MultipleCardsCarouselCard} [props.cards.mobileImage] - The mobile image for the card.
 * @param {MultipleCardsCarouselCard} [props.cards.desktopImage] - The desktop image for the card.
 * @returns {React.ReactElement}
 */
export default function MultipleCardsCarousel({
  eyebrow,
  title,
  cards,
  disclaimer,
}: MultipleCardsCarouselProps): React.ReactElement {
  const { handleMdpCtaClick } = useMdpModals();

  const handleClick = (params: HandleClickParams) => {
    const { clickEvent, label, cardTitle, ctaUrl } = params;
    handleMdpCtaClick({
      event: clickEvent,
      ctaUrl: ctaUrl || '',
    });

    trackCardClick(label, cardTitle);
  };

  const trackCardClick = (label?: string, cardTitle?: string | null) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'MultipleCardsCarousel',
        ctaText: label || 'No CTA label',
        cardTitle: cardTitle || 'No title provided',
      },
    });
  };

  const cardElements = cards
    ? cards?.map((card, index) => {
        const { tags, title, description, cta, mobileImage, desktopImage } =
          card;

        const hasFullUrl = isFullUrl(cta?.url);

        const linkTo = hasFullUrl
          ? replaceRedirectionLinkRoot(cta?.url || '')
          : cta?.url;

        const onButtonClick = (
          onButtonClickEvent: React.MouseEvent<
            HTMLButtonElement | HTMLAnchorElement
          >,
        ) => {
          handleClick({
            clickEvent: onButtonClickEvent,
            label: cta?.label,
            cardTitle: card?.title,
            ctaUrl: cta?.url,
          });

          if (hasFullUrl && linkTo) {
            window.location.href = linkTo;
            return;
          }

          // If the link is an internal link, use Gatsby navigate
          if (linkTo) {
            navigate(linkTo);
          }
        };

        const buttonProps: ButtonProps = {
          ...(hasFullUrl
            ? { href: linkTo, as: 'a' }
            : { to: linkTo, as: Link }),
          variant: 'primary',
          size: 'sm',
          colorScheme: 'whiteCS',
          id: `${cta?.label}-${card?.title}-card`,
          onClick: onButtonClick,
        };

        return (
          <ProductCardV2
            linkTo={linkTo}
            key={`card-${index}`}
            handleOnClick={(event) =>
              handleClick({
                clickEvent: event,
                label: cta?.label,
                cardTitle: card?.title,
                ctaUrl: cta?.url,
              })
            }
            size="sm"
            tag={
              tags && tags?.length ? (
                <Flex gap="0.5">
                  {tags.map((tag, index) => (
                    <ProductTag
                      key={`tag-${index}`}
                      displayText={tag?.displayText || ''}
                      metadata={tag?.metadata as Queries.ContentfulMetadata001}
                    />
                  ))}
                </Flex>
              ) : null
            }
            pricing={null}
            image={
              <>
                {mobileImage ? (
                  <Box
                    display={['block', null, 'none']}
                    padding="8"
                    height="100%"
                  >
                    <Image
                      as={GatsbyImage}
                      image={mobileImage?.gatsbyImageData}
                      margin="0 auto"
                      alt={mobileImage?.description || ''}
                      style={{ height: '100%' }}
                      imgStyle={{ objectFit: 'contain' }}
                    />
                  </Box>
                ) : null}

                {desktopImage ? (
                  <Box display={['none', null, 'block']} height="100%">
                    <Image
                      as={GatsbyImage}
                      image={desktopImage?.gatsbyImageData}
                      margin="0 auto"
                      alt={desktopImage?.description || ''}
                      style={{ height: '100%' }}
                      imgStyle={{ objectFit: 'contain' }}
                    />
                  </Box>
                ) : null}
              </>
            }
            centralizeCTA={true}
            cta={cta ? <Button {...buttonProps}>{cta.label}</Button> : null}
          >
            {title ? (
              <Heading
                size="md"
                color="tints.black"
                as="h3"
                textAlign="center"
                marginBottom="1"
              >
                {title}
              </Heading>
            ) : null}

            {description ? (
              <Text size="md" color="tints.darkGrey" textAlign="center">
                {description}
              </Text>
            ) : null}
          </ProductCardV2>
        );
      })
    : [];

  return (
    <Container
      size="fluid"
      paddingY={['10', '20']}
      backgroundColor="tints.lightCream"
    >
      <SectionContainer>
        <Box paddingX={[6, 10, 12]}>
          <SectionHeader eyebrow={eyebrow} title={title} />
        </Box>

        {!cards ? (
          <Skeleton height="400px" />
        ) : (
          <>
            {cards && cards.length > 0 ? (
              <MultipleCardsSwiper cards={cardElements} />
            ) : null}

            {disclaimer?.raw ? (
              <Box
                paddingX={[6, 10, 12]}
                marginTop={[6, 12]}
                marginLeft="auto"
                marginRight="auto"
                sx={{
                  padding: {
                    color: 'tints.uiGrey',
                    textAlign: 'center',
                    fontSize: 'sm',
                  },
                }}
              >
                <RichTextWrapper
                  contentfulRichText={disclaimer}
                ></RichTextWrapper>
              </Box>
            ) : null}
          </>
        )}
      </SectionContainer>
    </Container>
  );
}
